import React, { useState, useEffect } from 'react';
import QuickCustomerForm from './QuickCustomerForm';
import QuickVehicleForm from './QuickVehicleForm';
import QuickServiceForm from './QuickServiceForm';
import QuickPartSelector from './QuickPartSelector';
import PartForm from './PartForm';

const InvoiceForm = ({ invoice, customers, vehicles, services, parts, settings, quotes, onSubmit, onCancel, loading, onCreateCustomer, onCreateVehicle, onCreateService, onCreatePart, defaultCustomerId, defaultQuoteId }) => {
  const [formData, setFormData] = useState({
    invoice_number: '',
    customer_id: '',
    vehicle_id: '',
    quote_id: '',
    status: 'draft',
    due_date: '',
    payment_status: 'pending',
    payment_date: '',
    payment_method: '',
    notes: '',
    items: [],
    discount_type: '', // 'percent' | 'fixed'
    discount_value: 0,
    discount_label: '',
    tax_enabled: true
  });

  const [errors, setErrors] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [totals, setTotals] = useState({
    subtotal: 0,
    discount_total: 0,
    tax_amount: 0,
    total: 0
  });

  // États pour la création rapide
  const [showQuickCustomerForm, setShowQuickCustomerForm] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [showQuickVehicleForm, setShowQuickVehicleForm] = useState(false);
  const [creatingVehicle, setCreatingVehicle] = useState(false);
  const [showQuickServiceForm, setShowQuickServiceForm] = useState(false);
  const [creatingService, setCreatingService] = useState(false);

  // États pour le sélecteur de pièces
  const [showPartSelector, setShowPartSelector] = useState(false);
  
  // États pour la création de pièce rapide
  const [showQuickPartForm, setShowQuickPartForm] = useState(false);
  const [creatingPart, setCreatingPart] = useState(false);

  const statuses = [
    { value: 'draft', label: 'Brouillon', color: '#6b7280' },
    { value: 'sent', label: 'Envoyée', color: '#3b82f6' },
    { value: 'paid', label: 'Payée', color: '#10b981' },
    { value: 'overdue', label: 'En retard', color: '#ef4444' },
    { value: 'cancelled', label: 'Annulée', color: '#f59e0b' }
  ];

  const paymentStatuses = [
    { value: 'pending', label: 'En attente', color: '#f59e0b' },
    { value: 'partial', label: 'Partiel', color: '#3b82f6' },
    { value: 'paid', label: 'Payé', color: '#10b981' },
    { value: 'overdue', label: 'En retard', color: '#ef4444' }
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Espèces' },
    { value: 'check', label: 'Chèque' },
    { value: 'card', label: 'Carte bancaire' },
    { value: 'transfer', label: 'Virement' },
    { value: 'other', label: 'Autre' }
  ];

  useEffect(() => {
    if (invoice) {
      // Mode édition - charger les données existantes
      const dueDate = invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '';
      const paymentDate = invoice.payment_date ? new Date(invoice.payment_date).toISOString().split('T')[0] : '';
      
      setFormData({
        invoice_number: invoice.invoice_number || '',
        customer_id: invoice.customer_id || '',
        vehicle_id: invoice.vehicle_id || '',
        quote_id: invoice.quote_id || '',
        status: invoice.status || 'draft',
        due_date: dueDate,
        payment_status: invoice.payment_status || 'pending',
        payment_date: paymentDate,
        payment_method: invoice.payment_method || '',
        notes: invoice.notes || '',
        items: invoice.items || [],
        discount_type: invoice.discount_type || '',
        discount_value: Number(invoice.discount_value || 0),
        discount_label: invoice.discount_label || '',
        tax_enabled: typeof invoice.tax_enabled === 'boolean' ? invoice.tax_enabled : true
      });

      if (invoice.customer_id) {
        const customer = customers.find(c => c.id === invoice.customer_id);
        setSelectedCustomer(customer);
        updateAvailableVehicles(invoice.customer_id);
        updateAvailableQuotes(invoice.customer_id);
      }
    } else {
      // Mode création - générer le numéro et définir les valeurs par défaut
      if (settings) {
        const nextNumber = `${settings.invoice_prefix}-${String(settings.invoice_counter).padStart(4, '0')}`;
        setFormData(prev => ({ ...prev, invoice_number: nextNumber }));
      }

      // Définir la date d'échéance par défaut
      const paymentTerms = settings?.invoice_payment_terms || 30;
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + paymentTerms);
      setFormData(prev => ({ 
        ...prev, 
        due_date: dueDate.toISOString().split('T')[0] 
      }));

      // Prefill customer if provided
      if (defaultCustomerId) {
        setFormData(prev => ({ ...prev, customer_id: defaultCustomerId, vehicle_id: '', quote_id: '' }));
        const customer = customers.find(c => c.id === defaultCustomerId);
        setSelectedCustomer(customer || null);
        updateAvailableVehicles(defaultCustomerId);
        updateAvailableQuotes(defaultCustomerId);
      }

      // Prefill from quote if provided
      if (defaultQuoteId) {
        const selectedQuote = quotes.find(q => q.id === defaultQuoteId);
        if (selectedQuote) {
          const quoteItems = (selectedQuote.items || []).map(item => ({
            ...item,
            id: Date.now() + Math.random()
          }));
          setFormData(prev => ({
            ...prev,
            customer_id: selectedQuote.customer_id || prev.customer_id,
            vehicle_id: selectedQuote.vehicle_id || prev.vehicle_id,
            quote_id: selectedQuote.id,
            items: quoteItems
          }));
          setSelectedCustomer(customers.find(c => c.id === selectedQuote.customer_id) || null);
          updateAvailableVehicles(selectedQuote.customer_id);
          updateAvailableQuotes(selectedQuote.customer_id);
        }
      }
    }
  }, [invoice, customers, settings, quotes, defaultCustomerId, defaultQuoteId]);

  useEffect(() => {
    calculateTotals();
  }, [formData.items, formData.discount_type, formData.discount_value, formData.tax_enabled, settings]);

  const updateAvailableVehicles = (customerId) => {
    const customerVehicles = vehicles.filter(v => v.customer_id === customerId);
    setAvailableVehicles(customerVehicles);
  };

  const updateAvailableQuotes = (customerId) => {
    const customerQuotes = quotes.filter(q => 
      q.customer_id === customerId && 
      q.status === 'accepted'
      // Temporairement on retire le filtre invoiced car le champ n'existe peut-être pas encore
    );
    setAvailableQuotes(customerQuotes);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'customer_id') {
      const customer = customers.find(c => c.id === value);
      setSelectedCustomer(customer);
      updateAvailableVehicles(value);
      updateAvailableQuotes(value);
      setFormData(prev => ({ 
        ...prev, 
        vehicle_id: '', 
        quote_id: '',
        items: [] // Reset items when customer changes
      }));
    }

    if (name === 'quote_id' && value) {
      // Charger les items du devis sélectionné
      const selectedQuote = quotes.find(q => q.id === value);
      if (selectedQuote) {
        // Convertir les items du devis avec des IDs uniques pour la facture
        const quoteItems = (selectedQuote.items || []).map(item => ({
          ...item,
          id: Date.now() + Math.random() // Nouvel ID unique pour éviter les conflits
        }));
        
        setFormData(prev => ({
          ...prev,
          vehicle_id: selectedQuote.vehicle_id,
          items: quoteItems
        }));
      }
    }

    // Clear error for the changed field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      service_id: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: settings?.default_tax_rate || 20,
      total: 0
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateItem = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          
          // Auto-fill from service if service_id changes
          if (field === 'service_id' && value) {
            const service = services.find(s => s.id === value);
            if (service) {
              updatedItem.description = service.name;
              updatedItem.unit_price = service.base_price;
            }
          }
          
          // Calculate item total
          if (field === 'quantity' || field === 'unit_price') {
            updatedItem.total = updatedItem.quantity * updatedItem.unit_price;
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const removeItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.unit_price)), 0);
    const type = formData.discount_type;
    const val = Number(formData.discount_value || 0);
    let discount = 0;
    if (type === 'percent') {
      discount = subtotal * (val / 100);
    } else if (type === 'fixed') {
      discount = val;
    }
    discount = Math.max(0, Math.min(discount, subtotal));

    const taxRate = (settings?.default_tax_rate || 20) / 100;
    const taxBase = subtotal - discount;
    const taxAmount = formData.tax_enabled ? (taxBase * taxRate) : 0;
    const total = taxBase + taxAmount;

    setTotals({
      subtotal: subtotal,
      discount_total: discount,
      tax_amount: taxAmount,
      total: total
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_id) {
      newErrors.customer_id = 'Veuillez sélectionner un client';
    }

    if (!formData.vehicle_id) {
      newErrors.vehicle_id = 'Veuillez sélectionner un véhicule';
    }

    if (!formData.due_date) {
      newErrors.due_date = 'La date d\'échéance est requise';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'Ajoutez au moins une prestation';
    }

    // Validate items
    const itemErrors = formData.items.some(item => 
      !item.description || item.quantity <= 0 || item.unit_price < 0
    );

    if (itemErrors) {
      newErrors.items = 'Vérifiez les informations des prestations';
    }

    // Validate payment info if status is paid
    if (formData.payment_status === 'paid' && !formData.payment_date) {
      newErrors.payment_date = 'Date de paiement requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonctions pour la création rapide
  const handleQuickCustomerSubmit = async (customerData) => {
    setCreatingCustomer(true);
    try {
      const newCustomer = await onCreateCustomer(customerData);
      
      // Sélectionner automatiquement le nouveau client
      setFormData(prev => ({
        ...prev,
        customer_id: newCustomer.id
      }));
      
      // Mettre à jour le client sélectionné
      setSelectedCustomer(newCustomer);
      updateAvailableVehicles(newCustomer.id);
      updateAvailableQuotes(newCustomer.id);
      
      // Fermer le modal
      setShowQuickCustomerForm(false);
    } catch (error) {
      console.error('Erreur lors de la création du client:', error);
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleQuickVehicleSubmit = async (vehicleData) => {
    setCreatingVehicle(true);
    try {
      const newVehicle = await onCreateVehicle(vehicleData);
      
      // Sélectionner automatiquement le nouveau véhicule
      setFormData(prev => ({
        ...prev,
        vehicle_id: newVehicle.id
      }));
      
      // Mettre à jour les véhicules disponibles
      updateAvailableVehicles(formData.customer_id);
      
      // Fermer le modal
      setShowQuickVehicleForm(false);
    } catch (error) {
      console.error('Erreur lors de la création du véhicule:', error);
    } finally {
      setCreatingVehicle(false);
    }
  };

  const handleQuickServiceSubmit = async (serviceData) => {
    setCreatingService(true);
    try {
      const newService = await onCreateService(serviceData);
      
      // Fermer le modal
      setShowQuickServiceForm(false);
      
    } catch (error) {
      console.error('Erreur lors de la création du service:', error);
    } finally {
      setCreatingService(false);
    }
  };

  // Fonction pour créer une pièce rapide
  const handleQuickPartSubmit = async (partData) => {
    setCreatingPart(true);
    try {
      const newPart = await onCreatePart(partData);
      
      // Fermer le modal
      setShowQuickPartForm(false);
      
    } catch (error) {
      console.error('Erreur lors de la création de la pièce:', error);
    } finally {
      setCreatingPart(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = {
        ...formData,
        subtotal: totals.subtotal,
        discount_total: totals.discount_total,
        tax_amount: totals.tax_amount,
        total: totals.total,
        tax_rate: settings?.default_tax_rate || 20
      };
      onSubmit(submitData);
    }
  };

  // Fonction pour ajouter une pièce depuis le sélecteur
  const handleAddPart = (partItem) => {
    const newItem = {
      id: Date.now(),
      ...partItem
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setShowPartSelector(false);
  };

  return (
    <div className="invoice-form-overlay">
      <div className="invoice-form-container">
        <div className="invoice-form-header">
          <h2>{invoice ? 'Modifier la facture' : 'Nouvelle facture'}</h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="invoice-form">
          {/* En-tête de la facture */}
          <div className="form-section">
            <h3>Informations générales</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="invoice_number">Numéro de facture</label>
                <input
                  type="text"
                  id="invoice_number"
                  name="invoice_number"
                  value={formData.invoice_number}
                  onChange={handleChange}
                  placeholder="FACT-0001"
                  readOnly
                  className="readonly"
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Statut</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="customer_id">Client *</label>
                <div className="select-with-add">
                  <select
                    id="customer_id"
                    name="customer_id"
                    value={formData.customer_id}
                    onChange={handleChange}
                    className={errors.customer_id ? 'error' : ''}
                  >
                    <option value="">Sélectionner un client...</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} {customer.company && `(${customer.company})`}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="add-btn"
                    onClick={() => setShowQuickCustomerForm(true)}
                    title="Ajouter un nouveau client"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </button>
                </div>
                {errors.customer_id && <span className="error-text">{errors.customer_id}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="vehicle_id">Véhicule *</label>
                <div className="select-with-add">
                  <select
                    id="vehicle_id"
                    name="vehicle_id"
                    value={formData.vehicle_id}
                    onChange={handleChange}
                    disabled={!formData.customer_id}
                    className={errors.vehicle_id ? 'error' : ''}
                  >
                    <option value="">Sélectionner un véhicule...</option>
                    {availableVehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.brand} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
                        {vehicle.license_plate && ` - ${vehicle.license_plate}`}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="add-btn"
                    onClick={() => setShowQuickVehicleForm(true)}
                    disabled={!formData.customer_id}
                    title="Ajouter un nouveau véhicule"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </button>
                </div>
                {errors.vehicle_id && <span className="error-text">{errors.vehicle_id}</span>}
                {!formData.customer_id && (
                  <span className="info-text">Sélectionnez d'abord un client pour ajouter un véhicule</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote_id">Devis associé (optionnel)</label>
                <select
                  id="quote_id"
                  name="quote_id"
                  value={formData.quote_id}
                  onChange={handleChange}
                  disabled={!formData.customer_id}
                >
                  <option value="">Aucun devis / Facture libre</option>
                  {availableQuotes.map(quote => (
                    <option key={quote.id} value={quote.id}>
                      {quote.quote_number} - {quote.total?.toFixed(2) || '0.00'}€ ({quote.status})
                    </option>
                  ))}
                </select>
                {formData.customer_id && availableQuotes.length === 0 && (
                  <small style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                    Aucun devis accepté trouvé pour ce client
                  </small>
                )}
                {formData.customer_id && availableQuotes.length > 0 && (
                  <small style={{ color: '#059669', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                    {availableQuotes.length} devis disponible(s)
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="due_date">Date d'échéance *</label>
                <input
                  type="date"
                  id="due_date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  className={errors.due_date ? 'error' : ''}
                />
                {errors.due_date && <span className="error-text">{errors.due_date}</span>}
              </div>
            </div>
          </div>

          {/* Prestations */}
          <div className="form-section">
            <div className="section-header">
              <h3>Prestations</h3>
              <div className="section-actions">
                <button type="button" onClick={addItem} className="btn-add-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </svg>
                  Ajouter une prestation
                </button>
                <button
                  type="button"
                  className="btn-create-service"
                  onClick={() => setShowQuickServiceForm(true)}
                  title="Créer une nouvelle prestation"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                  </svg>
                  Nouvelle prestation
                </button>
                <button
                  type="button"
                  className="btn-add-part"
                  onClick={() => setShowPartSelector(true)}
                  title="Ajouter une pièce du stock"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  Ajouter une pièce
                </button>
                <button
                  type="button"
                  className="btn-create-part"
                  onClick={() => setShowQuickPartForm(true)}
                  title="Créer une nouvelle pièce"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                  </svg>
                  Nouvelle pièce
                </button>
              </div>
            </div>

            {formData.items.length > 0 ? (
              <div className="items-table">
                <div className="table-header">
                  <div>Service</div>
                  <div>Description</div>
                  <div>Qté</div>
                  <div>Prix unitaire</div>
                  <div>Total</div>
                  <div>Actions</div>
                </div>

                {formData.items.map((item) => (
                  <div key={item.id} className="table-row">
                    <div className="item-service">
                      <select
                        value={item.service_id}
                        onChange={(e) => updateItem(item.id, 'service_id', e.target.value)}
                      >
                        <option value="">Service personnalisé</option>
                        {services.filter(s => s.active).map(service => (
                          <option key={service.id} value={service.id}>
                            {service.name} ({service.base_price}€)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="item-description">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                        placeholder="Description de la prestation"
                      />
                    </div>

                    <div className="item-quantity">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                        onFocus={(e) => e.target.select()}
                        min="1"
                        step="1"
                      />
                    </div>

                    <div className="item-price">
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                        onFocus={(e) => e.target.select()}
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                      />
                    </div>

                    <div className="item-total">
                      {(item.quantity * item.unit_price).toFixed(2)}€
                    </div>

                    <div className="item-actions">
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="btn-remove-item"
                        title="Supprimer"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-items">
                <p>Aucune prestation ajoutée</p>
                <button type="button" onClick={addItem} className="btn-primary">
                  Ajouter la première prestation
                </button>
              </div>
            )}

            {errors.items && <span className="error-text">{errors.items}</span>}
          </div>

          {/* Totaux */}
          {formData.items.length > 0 && (
            <div className="form-section">
              <h3>Récapitulatif</h3>
              <div className="form-row" style={{ marginBottom: 8 }}>
                <div className="form-group">
                  <label>Type de remise</label>
                  <select
                    name="discount_type"
                    value={formData.discount_type}
                    onChange={handleChange}
                  >
                    <option value="">Aucune</option>
                    <option value="percent">Pourcentage</option>
                    <option value="fixed">Montant fixe</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Valeur</label>
                  <input
                    type="number"
                    name="discount_value"
                    step="0.01"
                    min="0"
                    value={formData.discount_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                    placeholder={formData.discount_type === 'percent' ? 'Ex: 10 (%)' : 'Ex: 50 (€)'}
                    disabled={!formData.discount_type}
                  />
                </div>
                <div className="form-group">
                  <label>Intitulé (optionnel)</label>
                  <input
                    type="text"
                    name="discount_label"
                    value={formData.discount_label}
                    onChange={handleChange}
                    placeholder="Ex: Promo"
                    disabled={!formData.discount_type}
                  />
                </div>
                <div className="form-group" style={{ alignSelf: 'end' }}>
                  <label style={{ display: 'block' }}>TVA applicable</label>
                  <input
                    type="checkbox"
                    checked={!!formData.tax_enabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, tax_enabled: e.target.checked }))}
                  />
                </div>
              </div>
              <div className="totals-section">
                <div className="totals-row">
                  <span>Sous-total HT :</span>
                  <span>{totals.subtotal.toFixed(2)}€</span>
                </div>
                {totals.discount_total > 0 && (
                  <div className="totals-row">
                    <span>
                      Remise{formData.discount_type === 'percent' ? ` (${Number(formData.discount_value || 0)}%)` : ''}{formData.discount_label ? ` - ${formData.discount_label}` : ''} :
                    </span>
                    <span>-{totals.discount_total.toFixed(2)}€</span>
                  </div>
                )}
                {formData.tax_enabled ? (
                  <div className="totals-row">
                    <span>TVA ({settings?.default_tax_rate || 20}%) :</span>
                    <span>{totals.tax_amount.toFixed(2)}€</span>
                  </div>
                ) : (
                  <div className="totals-row">
                    <span>TVA non applicable</span>
                    <span>-</span>
                  </div>
                )}
                <div className="totals-row total">
                  <span>Total TTC :</span>
                  <span>{totals.total.toFixed(2)}€</span>
                </div>
              </div>
            </div>
          )}

          {/* Section paiement */}
          <div className="form-section">
            <h3>Informations de paiement</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="payment_status">Statut de paiement</label>
                <select
                  id="payment_status"
                  name="payment_status"
                  value={formData.payment_status}
                  onChange={handleChange}
                >
                  {paymentStatuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="payment_method">Mode de paiement</label>
                <select
                  id="payment_method"
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                >
                  <option value="">Sélectionner...</option>
                  {paymentMethods.map(method => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {formData.payment_status === 'paid' && (
              <div className="form-group">
                <label htmlFor="payment_date">Date de paiement *</label>
                <input
                  type="date"
                  id="payment_date"
                  name="payment_date"
                  value={formData.payment_date}
                  onChange={handleChange}
                  className={errors.payment_date ? 'error' : ''}
                />
                {errors.payment_date && <span className="error-text">{errors.payment_date}</span>}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="form-section">
            <h3>Notes</h3>
            <div className="form-group">
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Notes sur la facture, conditions de paiement particulières..."
                rows="4"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Enregistrement...' : (invoice ? 'Mettre à jour' : 'Créer la facture')}
            </button>
          </div>
        </form>

        <style jsx>{`
          .invoice-form-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .invoice-form-container {
            background: white;
            border-radius: 12px;
            width: 100%;
            max-width: 1000px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .invoice-form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 24px 0 24px;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 24px;
            position: sticky;
            top: 0;
            background: white;
            z-index: 10;
          }

          .invoice-form-header h2 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
          }

          .close-btn {
            background: none;
            border: none;
            color: #6b7280;
            cursor: pointer;
            padding: 8px;
            border-radius: 6px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: #f3f4f6;
            color: #374151;
          }

          .invoice-form {
            padding: 0 24px 24px 24px;
          }

          .form-section {
            margin-bottom: 32px;
            padding-bottom: 24px;
            border-bottom: 1px solid #f3f4f6;
          }

          .form-section:last-of-type {
            border-bottom: none;
          }

          .form-section h3 {
            margin: 0 0 20px 0;
            font-size: 1.2rem;
            font-weight: 600;
            color: #1f2937;
            border-left: 4px solid #3b82f6;
            padding-left: 12px;
          }

          .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }

          .section-actions {
            display: flex;
            gap: 12px;
            align-items: center;
          }

          .section-header h3 {
            margin: 0;
          }

          .btn-add-item {
            background: #10b981;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 8px 16px;
            font-size: 0.9rem;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .btn-add-item:hover {
            background: #059669;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 16px;
          }

          @media (max-width: 640px) {
            .form-row {
              grid-template-columns: 1fr;
            }
          }

          .form-group {
            position: relative;
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
            color: #374151;
            font-size: 0.9rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            width: 100%;
            padding: 12px 16px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-size: 0.9rem;
            transition: all 0.2s ease;
            box-sizing: border-box;
          }

          .form-group input:focus,
          .form-group select:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .form-group input.error,
          .form-group select.error,
          .form-group textarea.error {
            border-color: #dc2626;
          }

          .form-group input.readonly {
            background: #f9fafb;
            color: #6b7280;
          }

          .form-group textarea {
            resize: vertical;
            min-height: 100px;
          }

          .error-text {
            color: #dc2626;
            font-size: 0.8rem;
            margin-top: 4px;
            display: block;
          }

          .items-table {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            overflow: hidden;
          }

          .table-header {
            display: grid;
            grid-template-columns: 200px 1fr 80px 120px 100px 60px;
            background: #f9fafb;
            padding: 12px;
            font-weight: 600;
            color: #374151;
            font-size: 0.9rem;
            border-bottom: 1px solid #e5e7eb;
          }

          .table-row {
            display: grid;
            grid-template-columns: 200px 1fr 80px 120px 100px 60px;
            padding: 12px;
            border-bottom: 1px solid #f3f4f6;
            align-items: center;
            gap: 8px;
          }

          .table-row:last-child {
            border-bottom: none;
          }

          .item-service select,
          .item-description input,
          .item-quantity input,
          .item-price input {
            width: 100%;
            padding: 8px;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            font-size: 0.85rem;
          }

          .item-total {
            font-weight: 600;
            color: #059669;
            text-align: right;
          }

          .btn-remove-item {
            background: #fee2e2;
            color: #dc2626;
            border: none;
            border-radius: 4px;
            padding: 6px;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .btn-remove-item:hover {
            background: #fecaca;
          }

          .empty-items {
            text-align: center;
            padding: 40px;
            color: #6b7280;
          }

          .empty-items p {
            margin: 0 0 16px 0;
          }

          /* Responsive: éviter le débordement des boutons d'actions */
          @media (max-width: 768px) {
            .section-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 12px;
            }
            .section-actions {
              width: 100%;
              flex-wrap: wrap;
              gap: 10px;
            }
            .section-actions > button {
              flex: 1 1 calc(50% - 10px);
              white-space: nowrap;
            }
          }

          @media (max-width: 480px) {
            .section-actions > button { flex: 1 1 100%; }
          }

          .btn-primary {
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 8px;
            padding: 12px 24px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .btn-primary:hover {
            background: #2563eb;
          }

          .totals-section {
            background: #f9fafb;
            padding: 20px;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }

          .totals-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            font-size: 0.9rem;
          }

          .totals-row.total {
            font-weight: 600;
            font-size: 1.1rem;
            color: #1f2937;
            padding-top: 8px;
            border-top: 1px solid #d1d5db;
            margin-top: 8px;
          }

          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 32px;
            padding-top: 24px;
            border-top: 1px solid #e5e7eb;
            position: sticky;
            bottom: 0;
            background: white;
          }

          .btn-cancel,
          .btn-submit {
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            font-size: 0.9rem;
          }

          .btn-cancel {
            background: #f3f4f6;
            color: #374151;
          }

          .btn-cancel:hover {
            background: #e5e7eb;
          }

          .btn-submit {
            background: #3b82f6;
            color: white;
          }

          .btn-submit:hover:not(:disabled) {
            background: #2563eb;
          }

          .btn-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          /* Styles pour les boutons de création rapide */
          .select-with-add {
            display: flex;
            gap: 8px;
            align-items: flex-start;
          }

          .select-with-add select {
            flex: 1;
          }

          .add-btn {
            background: #10b981;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 10px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            flex-shrink: 0;
            height: 42px;
            width: 42px;
          }

          .add-btn:hover {
            background: #059669;
            transform: scale(1.05);
          }

          .add-btn:active {
            transform: scale(0.95);
          }

          .add-btn:disabled {
            background: #9ca3af;
            cursor: not-allowed;
            transform: none;
          }

          .add-btn:disabled:hover {
            background: #9ca3af;
            transform: none;
          }

          .info-text {
            color: #6b7280;
            font-size: 0.8rem;
            margin-top: 4px;
            font-style: italic;
          }

          /* Largeurs des modals harmonisées avec PartForm */
          .quick-customer-modal,
          .quick-vehicle-modal,
          .quick-service-modal {
            max-width: 800px;
            width: 90vw;
          }

          /* Styles pour les grilles des formulaires */
          .quick-customer-form,
          .quick-vehicle-form,
          .quick-service-form {
            padding: 24px;
          }

          .quick-customer-form .form-grid,
          .quick-vehicle-form .form-grid,
          .quick-service-form .form-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-bottom: 24px;
          }

          .quick-customer-form .full-width,
          .quick-vehicle-form .full-width,
          .quick-service-form .full-width {
            grid-column: 1 / -1;
          }

          /* Amélioration des champs de formulaire pour tous les modals */
          .quick-customer-form .form-group,
          .quick-vehicle-form .form-group,
          .quick-service-form .form-group {
            margin-bottom: 4px;
          }

          .quick-customer-form label,
          .quick-vehicle-form label,
          .quick-service-form label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
            color: #374151;
            font-size: 0.875rem;
          }

          .quick-customer-form input,
          .quick-vehicle-form input,
          .quick-service-form input,
          .quick-customer-form select,
          .quick-vehicle-form select,
          .quick-service-form select {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
          }

          .quick-customer-form input:focus,
          .quick-vehicle-form input:focus,
          .quick-service-form input:focus,
          .quick-customer-form select:focus,
          .quick-vehicle-form select:focus,
          .quick-service-form select:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .quick-customer-form .error-message,
          .quick-vehicle-form .error-message,
          .quick-service-form .error-message {
            color: #ef4444;
            font-size: 0.75rem;
            margin-top: 4px;
            display: block;
          }

          .section-actions {
            display: flex;
            gap: 12px;
            align-items: center;
          }

          .btn-create-service {
            background: #8b5cf6;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 8px 16px;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            transition: all 0.2s ease;
          }

          .btn-create-service:hover {
            background: #7c3aed;
            transform: translateY(-1px);
          }

          .btn-create-service:active {
            transform: translateY(0);
          }

          .btn-add-part {
            background: #f59e0b;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 8px 16px;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            transition: all 0.2s ease;
          }

          .btn-add-part:hover {
            background: #d97706;
            transform: translateY(-1px);
          }

          .btn-add-part:active {
            transform: translateY(0);
          }

          .btn-create-part {
            background: #10b981;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 8px 16px;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            transition: all 0.2s ease;
          }

          .btn-create-part:hover {
            background: #059669;
            transform: translateY(-1px);
          }

          .btn-create-part:active {
            transform: translateY(0);
          }

          @media (max-width: 768px) {
            .table-header,
            .table-row {
              grid-template-columns: 1fr;
              gap: 8px;
            }

            .table-header {
              display: none;
            }

            .table-row {
              display: flex;
              flex-direction: column;
              padding: 16px;
            }
          }
        `}</style>
      </div>

      {/* Modal de création client rapide */}
      {showQuickCustomerForm && (
        <QuickCustomerForm
          onSubmit={handleQuickCustomerSubmit}
          onCancel={() => setShowQuickCustomerForm(false)}
          loading={creatingCustomer}
        />
      )}

      {/* Modal de création véhicule rapide */}
      {showQuickVehicleForm && (
        <QuickVehicleForm
          customerId={formData.customer_id}
          onSubmit={handleQuickVehicleSubmit}
          onCancel={() => setShowQuickVehicleForm(false)}
          loading={creatingVehicle}
        />
      )}

      {/* Modal de création service rapide */}
      {showQuickServiceForm && (
        <QuickServiceForm
          onSubmit={handleQuickServiceSubmit}
          onCancel={() => setShowQuickServiceForm(false)}
          loading={creatingService}
        />
      )}

      {/* Modal de sélection de pièces */}
      {showPartSelector && (
        <QuickPartSelector
          parts={parts || []}
          onSubmit={handleAddPart}
          onCancel={() => setShowPartSelector(false)}
          loading={false}
        />
      )}

      {/* Modal de création pièce rapide */}
      {showQuickPartForm && (
        <PartForm
          onSave={handleQuickPartSubmit}
          onCancel={() => setShowQuickPartForm(false)}
          loading={creatingPart}
        />
      )}
    </div>
  );
};

export default InvoiceForm;
